import makeStyles from '@mui/styles/makeStyles';
import classNames from "classnames";
import {orderSessionSteps} from "business/session";
import {history} from 'common/history';
import {withPreviewQuery} from "common/preview";
import {PAGE_HEIGHT_WITH_NAVBAR} from "common/helpers";
import {color, font, space, type} from "common/tokens";
import {useSetTranslation} from "common/useSetTranslation";
import PreviewBanner from "components/PreviewBanner";
import CtaButton from "components/redesign/CtaButton";
import Skeleton from "components/redesign/Skeleton";
import {every, filter, findIndex, flatten, get, isEmpty, isUndefined, map, sortBy, toNumber, toPairs} from "lodash";
import React, {useEffect, useLayoutEffect, useRef} from 'react';
import {useTranslation} from "react-i18next";
import SwipeableViews from "components/SwipeableViews";
import SessionStep from "views/session/SessionStep";

// The step name and its progress sit in a bar pinned to the top of the page —
// the name doubles as the way back to the session — with only the step content
// scrolling between that bar and the Previous / Next step footer.
const useStyles = makeStyles({
  page: {
    background: color.SURFACE,
    height: PAGE_HEIGHT_WITH_NAVBAR,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  container: {
    width: '100%',
    maxWidth: space.CONTAINER_MAX,
    margin: '0 auto',
    padding: '0 32px',
    boxSizing: 'border-box',
  },
  column: {
    width: 638,
    maxWidth: '100%',
    margin: '0 auto',
  },
  topBar: {
    flexShrink: 0,
    background: color.SURFACE,
    paddingBottom: 18,
  },
  back: {
    ...type.body,
    fontSize: 18,
    lineHeight: '26px',
    fontWeight: font.SEMIBOLD,
    color: color.TEXT,
    appearance: 'none',
    background: 'none',
    border: 'none',
    width: '100%',
    padding: '14px 0 16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    textAlign: 'left',
    gap: 10,
    cursor: 'pointer',
  },
  backArrow: {
    color: color.TEXT_MUTED,
    fontSize: 20,
    lineHeight: 1,
    flexShrink: 0,
  },
  progressTrack: {
    height: 4,
    borderRadius: 4,
    background: color.DIVIDER,
    overflow: 'hidden',
  },
  progressFill: {
    display: 'block',
    height: '100%',
    borderRadius: 4,
    background: color.ACCENT,
  },
  scroller: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    // Without this Chrome anchors to the outgoing step and restores the offset
    // as the next one mounts, putting back the scroll we just reset.
    overflowAnchor: 'none',
  },
  footer: {
    flexShrink: 0,
    borderTop: `1px solid ${color.DIVIDER}`,
  },
  footerInner: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '10px 0',
  },
  spacer: {
    flex: 1,
  },
  // Holds the shape of the step — the bar's title, then the content — while
  // the session is on its way.
  titleSkeleton: {
    padding: '14px 0 16px',
  },
  contentSkeleton: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    paddingTop: 24,
  },
});

const MentorshipSessionStepWrapper = ({
  match,
  session,
  role,
  initQuiz,
  stepCompleted,
  initQuizAnswers,
  currentMentorshipSessionStatus,
  quizAnswers,
  partnerDetails,
  startSession,
  initSession,
  isPreview
}) => {
  const classes = useStyles();
  const {t} = useTranslation();
  const {st} = useSetTranslation();
  const sessionIdFromRoute = get(match, 'params.sessionId');

  useEffect(() => {
    if (!sessionIdFromRoute) return;
    if (isPreview) {
      initSession(sessionIdFromRoute, false);
    } else {
      startSession(sessionIdFromRoute, false);
      initSession(sessionIdFromRoute, false);
    }
  }, [sessionIdFromRoute, isPreview]);

  const orderedSessionSteps = orderSessionSteps(session);
  const getIndex = (stepId) => findIndex(orderedSessionSteps, (step) => step.id === stepId);

  const stepId = toNumber(match.params.stepId);
  const currentStep = getIndex(stepId);

  const orderStatusSteps = (steps) => sortBy(steps, (step) => step.order);

  const allPreviousStepsCompleted = (steps) => {
    const orderedSteps = orderStatusSteps(orderStatusSteps(steps));
    for (let i = 0; i < orderedSteps.length - 1; i++) {
      if (orderedSteps[i].status !== "COMPLETED") {
        return false;
      }
    }
    return true;
  };

  const isFirstRunOfSession = (s) =>
    !(s.status === "PARTIAL_COMPLETE" || s.status === "COMPLETED");

  const quizHasMandatoryQuestions = (quiz) =>
    flatten(quiz.questions).some(q => q.mandatory === true);

  const isStepCompleted = (stepIdx, qa, sid) => {
    const step = orderedSessionSteps[stepIdx];
    const stepContent = get(step, 'content');
    const mandatoryQuizContents = filter(
      stepContent,
      (content) => content.contentType.toLowerCase() === 'quiz' && quizHasMandatoryQuestions(content.quiz)
    );
    const userAnswersForMandatoryQuizContents = mandatoryQuizContents.map((quiz) =>
      get(qa, `[${sid}][${step.id}][${quiz.quiz.id}].answers.userAnswer`)
    );
    if (mandatoryQuizContents.length > 0) {
      return get(toPairs(userAnswersForMandatoryQuizContents[0]), '[0][1]', []).length > 0;
    }
    return true;
  };

  const isLastPage = currentStep === session.sessionSteps.length - 1;
  const canLeaveStep = isPreview || isStepCompleted(currentStep, quizAnswers, session.id);

  const setSessionStep = (sid) => {
    const path = `/mentorship/${session.id}/step/${sid}`;
    history.push(withPreviewQuery(path, !!isPreview));
  };

  const previousStepChange = (stepIndex) => {
    setSessionStep(orderedSessionSteps[stepIndex].id);
  };

  const handleStepChange = (currentStepIndex) => {
    if (currentStepIndex < currentStep) {
      previousStepChange(currentStepIndex);
    } else if (currentStepIndex > currentStep) {
      setSessionStep(orderedSessionSteps[currentStepIndex].id);
    }
    if (!isPreview) {
      stepCompleted(session, orderedSessionSteps[currentStep], quizAnswers);
    }
  };

  const nextStepChange = (currentStepIndex, qa, sid) => {
    if (isPreview || isStepCompleted(currentStepIndex, qa, sid)) {
      let step = currentStepIndex + 1;
      const nextStep = step < 0 ? 0 : step > orderedSessionSteps.length - 1 ? orderedSessionSteps.length - 1 : step;
      setSessionStep(orderedSessionSteps[nextStep].id);
      if (!isPreview && !isLastPage) {
        stepCompleted(session, orderedSessionSteps[currentStepIndex], qa);
      }
    }
  };

  // Opened directly on a step, the status call has not landed yet — and Done is
  // a claim about what the server has recorded, so it waits for it.
  const showDone =
    !isPreview &&
    !isUndefined(currentMentorshipSessionStatus) &&
    isLastPage &&
    isFirstRunOfSession(currentMentorshipSessionStatus) &&
    allPreviousStepsCompleted(get(currentMentorshipSessionStatus, 'steps')) &&
    isStepCompleted(currentStep, quizAnswers, session.id);

  // Every step already done on the server: the session has been finished before,
  // so the last page offers a way out rather than marking it done again.
  const steps = get(currentMentorshipSessionStatus, 'steps');
  const isSessionAlreadyComplete =
    !isEmpty(steps) && every(steps, step => step.status === 'COMPLETED');

  const handleDoneClick = () => {
    if (isPreview) return;
    stepCompleted(
      session,
      orderedSessionSteps[currentStep],
      isStepCompleted(currentStep, quizAnswers, session.id) && quizAnswers
    );
    history.push(
      withPreviewQuery(`/mentorship/${session.id}/step/sessionfeedback/`, false),
      {sessionId: session.id}
    );
  };

  // Preview belongs to reading a step, not to the listing — carrying the flag
  // back left the banner on a page the reader had already stepped out of.
  const goBack = () => history.replace(`/mentorship/${session.id}/summary`);

  // The step content keeps its own scroll, so moving to the next step would
  // otherwise open it halfway down wherever the last one was left. Jumped
  // before paint rather than scrolled, so the new step is simply already at
  // the top instead of travelling there while it is read.
  const scrollerRef = useRef(null);
  useLayoutEffect(() => {
    if (scrollerRef.current) scrollerRef.current.scrollTop = 0;
  }, [stepId]);

  if (isUndefined(session.id)) {
    return (
      <div className={classes.page}>
        <div className={classes.topBar}>
          <div className={classes.container}>
            <div className={classes.column}>
              <div className={classes.titleSkeleton}>
                <Skeleton width={220} height={26}/>
              </div>
              <div className={classes.progressTrack}/>
            </div>
          </div>
        </div>
        <div className={classes.scroller}>
          <div className={classes.container}>
            <div className={classNames(classes.column, classes.contentSkeleton)}>
              <Skeleton width={'55%'} height={28}/>
              <Skeleton height={220}/>
              <Skeleton height={16}/>
              <Skeleton width={'85%'} height={16}/>
              <Skeleton width={'70%'} height={16}/>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const currentStepData = orderedSessionSteps[currentStep];
  const stepTitle = st(get(currentStepData, 'title'), get(currentStepData, 'localeTitle'));

  return (
    <div className={classes.page}>
      {/* Stays put while the step content scrolls under it. */}
      <div className={classes.topBar}>
        <div className={classes.container}>
          {isPreview && <PreviewBanner/>}
          <div className={classes.column}>
            <button
              type={'button'}
              className={classes.back}
              onClick={goBack}
              aria-label={t('backToSession', {title: st(session.title, session.localeTitle)})}
            >
              <span className={classes.backArrow} aria-hidden={true}>{'←'}</span>
              {stepTitle}
            </button>
            <div className={classes.progressTrack}>
              <span
                className={classes.progressFill}
                style={{width: `${((currentStep + 1) * 100) / orderedSessionSteps.length}%`}}
              />
            </div>
          </div>
        </div>
      </div>

      <div className={classes.scroller} ref={scrollerRef}>
        <div className={classes.container}>
          <div className={classes.column}>
            <SwipeableViews
              index={currentStep}
              enableMouseEvents
              disabled={!canLeaveStep}
              onChangeIndex={(i) => handleStepChange(i)}
              slideStyle={{overflow: 'hidden'}}
            >
              {map(orderedSessionSteps, (step, index) => (
                <SessionStep
                  key={step.id}
                  step={step}
                  sessionId={session.id}
                  role={role}
                  initQuiz={initQuiz}
                  quizAnswers={quizAnswers}
                  isLearningHub={session.isLearningHub}
                  partnerDetails={partnerDetails}
                  isPreview={isPreview}
                  initQuizAnswers={initQuizAnswers}
                  isInView={index === currentStep}
                />
              ))}
            </SwipeableViews>
          </div>
        </div>
      </div>

      <div className={classes.footer}>
        <div className={classes.container}>
          <div className={classNames(classes.column, classes.footerInner)}>
            {currentStep > 0 && (
              <CtaButton
                size={'large'}
                variant={'secondary'}
                label={`← ${t('previous')}`}
                onClick={() => previousStepChange(currentStep - 1)}
              />
            )}
            <span className={classes.spacer}/>
            {isLastPage && isPreview ? (
              <CtaButton
                size={'large'}
                variant={'primary'}
                label={t('exitPreview')}
                onClick={goBack}
              />
            ) : showDone ? (
              <CtaButton
                size={'large'}
                variant={'primary'}
                label={t('markAsDone')}
                onClick={handleDoneClick}
              />
            ) : isLastPage ? (
              isSessionAlreadyComplete && (
                <CtaButton
                  size={'large'}
                  variant={'secondary'}
                  label={t('closeSession')}
                  onClick={() => history.push('/mentorship')}
                />
              )
            ) : (
              <CtaButton
                size={'large'}
                variant={'primary'}
                label={t('nextStep')}
                disabled={!canLeaveStep}
                onClick={() => nextStepChange(currentStep, quizAnswers, session.id)}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  )
};

export default MentorshipSessionStepWrapper;
